"use client";

import { useEffect, useRef, useState } from "react";
import { PencilLine, X } from "lucide-react";
import {
  type WorkspaceAvatarSelection,
} from "@/lib/workspaceAvatar";
import { WorkspaceAvatarDisplay } from "@/app/components/WorkspaceAvatarDisplay";
import { WorkspaceAvatarPickerModal } from "@/app/components/WorkspaceAvatarPickerModal";
import { AeraButton } from "@/app/components/ui/AeraButton";

export type WorkspaceNameIconInputProps = {
  name: string;
  onNameChange: (value: string) => void;
  avatar: WorkspaceAvatarSelection;
  onAvatarChange: (avatar: WorkspaceAvatarSelection) => void;
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
  required?: boolean;
};

export function WorkspaceNameIconInput({
  name,
  onNameChange,
  avatar,
  onAvatarChange,
  placeholder = "ใส่ชื่อเวิร์กสเปซ...",
  autoFocus = true,
  className = "",
  required = false,
}: WorkspaceNameIconInputProps) {
  const avatarButtonRef = useRef<HTMLButtonElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [isNameFocused, setIsNameFocused] = useState(false);
  const [livePreview, setLivePreview] =
    useState<WorkspaceAvatarSelection | null>(null);

  const displayedAvatar = pickerOpen && livePreview ? livePreview : avatar;
  const hasName = name.trim().length > 0;

  useEffect(() => {
    if (!autoFocus) return;
    const timer = window.setTimeout(() => {
      nameInputRef.current?.focus({ preventScroll: true });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [autoFocus]);

  return (
    <>
      <div
        className={[
          "grid grid-cols-[40px_1fr_auto] items-center gap-1 rounded-lg border p-1 transition-colors",
          isNameFocused
            ? "border-aera-amber bg-aera-warmcream/40 ring-2 ring-aera-amber/15 shadow-[0_0_15px] shadow-aera-amber/30"
            : "border-aera-amber/25 bg-aera-warmcream/40",
          className,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className="max-w-10 max-h-10 shrink-0 relative group">
          <AeraButton
            ref={avatarButtonRef}
            variant="ghost"
            size="xs"
            type="button"
            paddingSize="none"
            className="w-10 h-10 rounded-lg! overflow-hidden bg-aera-white shadow-sm"
            onClick={() => {
              setLivePreview(avatar);
              setPickerOpen(true);
            }}
            aria-label="เลือกรูปหรือไอคอนเวิร์กสเปซ"
          >
            <WorkspaceAvatarDisplay avatar={displayedAvatar} size={40} />
          </AeraButton>
        </div>
        <div className="flex-1 relative">
          <input
            ref={nameInputRef}
            type="text"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            onFocus={() => setIsNameFocused(true)}
            onBlur={() => setIsNameFocused(false)}
            placeholder={placeholder}
            className="w-full h-10 bg-aera-transparent border-0 text-aera-charcoal text-sm font-semibold rounded-xl ps-2 pe-0 py-3 focus:outline-none placeholder:text-aera-amber/70 focus:placeholder:text-aera-amber/90"
            autoFocus={autoFocus}
            required={required}
          />
        </div>
        <div className="flex h-8 w-8 shrink-0 items-center justify-center">
          {hasName ? (
            <AeraButton
              variant="ghost"
              size="xs"
              type="button"
              onClick={() => onNameChange("")}
              className="h-8! w-8! p-0! text-aera-gray hover:text-aera-charcoal"
              aria-label="ล้างชื่อเวิร์กสเปซ"
            >
              <X size={14} strokeWidth={2} />
            </AeraButton>
          ) : (
            <span
              className={`pointer-events-none ${isNameFocused ? "text-aera-amber" : "text-aera-gray"}`}
            >
              <PencilLine size={14} strokeWidth={2} />
            </span>
          )}
        </div>
      </div>

      <WorkspaceAvatarPickerModal
        isOpen={pickerOpen}
        onClose={() => {
          setPickerOpen(false);
          setLivePreview(null);
        }}
        value={avatar}
        onChange={(next) => {
          onAvatarChange(next);
          setLivePreview(null);
        }}
        onDraftPreviewChange={setLivePreview}
        anchorRef={avatarButtonRef}
      />
    </>
  );
}
